// Module-level helper functions for localization

export type CurrencyOption = {
  code: string;
  name: string;
  symbol: string;
};

export const currencyList: CurrencyOption[] = [
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "CHF", name: "Swiss Franc", symbol: "CHF" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
  { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
  { code: "SAR", name: "Saudi Riyal", symbol: "﷼" },
];

export const getCurrencyByCode = (code: string): CurrencyOption | undefined => {
  return currencyList.find((currency) => currency.code === code);
};

export const formatCurrencyDisplay = (code: string): string => {
  const currency = getCurrencyByCode(code);
  if (currency) {
    return `${currency.symbol} ${currency.code}`;
  }
  return code;
};

export type LanguageOption = {
  code: string;
  name: string;
  nativeName: string;
};

export const languageList: LanguageOption[] = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  { code: "bn", name: "Bengali", nativeName: "বাংলা" },
  { code: "te", name: "Telugu", nativeName: "తెలుగు" },
  { code: "mr", name: "Marathi", nativeName: "मराठी" },
  { code: "ta", name: "Tamil", nativeName: "தமிழ்" },
  { code: "ur", name: "Urdu", nativeName: "اردو" },
  { code: "gu", name: "Gujarati", nativeName: "ગુજરાતી" },
  { code: "kn", name: "Kannada", nativeName: "ಕನ್ನಡ" },
  { code: "or", name: "Odia", nativeName: "ଓଡ଼ିଆ" },
  { code: "pa", name: "Punjabi", nativeName: "ਪੰਜਾਬੀ" },
  { code: "ml", name: "Malayalam", nativeName: "മലയാളം" },
  { code: "as", name: "Assamese", nativeName: "অসমীয়া" },
];

export const getLanguageByCode = (code: string): LanguageOption | undefined => {
  return languageList.find((language) => language.code === code);
};

export const formatLanguageDisplay = (code: string): string => {
  const language = getLanguageByCode(code);
  if (language) {
    return `${language.nativeName} (${language.code.toUpperCase()})`;
  }
  return code;
};

// Date Format Options
export type DateFormatOption = {
  code: string;
  name: string;
  example: string;
};

export const dateFormatList: DateFormatOption[] = [
  { code: "DD/MM/YYYY", name: "DD/MM/YYYY", example: "25/12/2024" },
  { code: "MM/DD/YYYY", name: "MM/DD/YYYY", example: "12/25/2024" },
  { code: "YYYY-MM-DD", name: "YYYY-MM-DD", example: "2024-12-25" },
  { code: "DD-MM-YYYY", name: "DD-MM-YYYY (Indian)", example: "25-12-2024" },
  { code: "DD/MM/YY", name: "DD/MM/YY", example: "25/12/24" },
];

export const getDateFormatByCode = (
  code: string,
): DateFormatOption | undefined => {
  return dateFormatList.find((format) => format.code === code);
};

export const formatDateFormatDisplay = (code: string): string => {
  const format = getDateFormatByCode(code);
  if (format) {
    return `${format.name} (e.g., ${format.example})`;
  }
  return code;
};

// Time Format Options
export type TimeFormatOption = {
  code: string;
  name: string;
  example: string;
};

export const timeFormatList: TimeFormatOption[] = [
  { code: "12h", name: "12-hour format", example: "02:30 PM" },
  { code: "24h", name: "24-hour format", example: "14:30" },
];

export const getTimeFormatByCode = (
  code: string,
): TimeFormatOption | undefined => {
  return timeFormatList.find((format) => format.code === code);
};

export const formatTimeFormatDisplay = (code: string): string => {
  const format = getTimeFormatByCode(code);
  if (format) {
    return `${format.name} (e.g., ${format.example})`;
  }
  return code;
};

// Number Format Options
export type NumberFormatOption = {
  code: string;
  name: string;
  example: string;
};

export const numberFormatList: NumberFormatOption[] = [
  {
    code: "en-IN",
    name: "Indian numbering system (Lakhs, Crores)",
    example: "1,00,000",
  },
  { code: "en-US", name: "International format", example: "100,000" },
  { code: "en-GB", name: "UK format", example: "100,000" },
];

export const getNumberFormatByCode = (
  code: string,
): NumberFormatOption | undefined => {
  return numberFormatList.find((format) => format.code === code);
};

export const formatNumberFormatDisplay = (code: string): string => {
  const format = getNumberFormatByCode(code);
  if (format) {
    return `${format.name} (e.g., ${format.example})`;
  }
  return code;
};

// Format Preview Functions
export const getDateFormatPreview = (code: string): string => {
  const format = getDateFormatByCode(code);
  return format ? format.example : "N/A";
};

export const getTimeFormatPreview = (code: string): string => {
  const format = getTimeFormatByCode(code);
  return format ? format.example : "N/A";
};

export const getNumberFormatPreview = (code: string): string => {
  const format = getNumberFormatByCode(code);
  return format ? format.example : "N/A";
};
